// Components
import CustomIcon from "components/CustomIcon";
import VcpImportance from "components/Projection/VcpImportance";
import MessageUpgrade from "components/Pro/Messages/MessageUpgrade/index";
import { Grid, Button, IconButton, Typography, Box, Switch, FormControlLabel } from "@mui/material";

// Hooks
import { useIntl } from "react-intl";
import { useAppSelector } from "app/hooks";
import useCharts from "./useCharts";

// Store
import { getCharts } from "store/slice/charts";

// Ability
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";

// Others
import icons from "enums/icons";

// Utils
import classnames from "classnames";

// Styles
import ChartStyles from "./Charts.styles";

const Charts = (): JSX.Element => {
    const intl = useIntl();
    const ability = useAbility(AbilityContext);
    const allChartData = useAppSelector(getCharts).data;

    const isFreeRider = ability.can("see", "LD_FREE") || ability.can("see", "LD_LIMITED");
    const classes = ChartStyles({ isFreeRider });

    const {
        isFetchingCharts,
        width,
        height,
        reportChart,
        shouldShowPaceDefinitions,
        chartWrapper,
        handleOnClickInsightsLink,
        goToReport,
        insightsSectionRef,
        setShouldShowPaceDefinitions,
    } = useCharts();

    const canSeeChart = reportChart ? ability.can("see", reportChart.subject) : false;
    const chartExplainer = reportChart && allChartData?.graphs[reportChart.id]?.raw?.leadership_insight?.explainer;

    return (
        <>
            <Grid container className={classes.headerContainer}>
                <Grid container item className={classes.navBox}>
                    <Grid container item className={classnames(classes.navControls, classes.navBtnLeft)}>
                        <IconButton onClick={() => goToReport("previous")} className={classes.navBtnLeft} size="medium">
                            <CustomIcon className={classes.navControlIcon} icon={icons.circledChevronLeftThin} />
                        </IconButton>
                    </Grid>
                    <Grid item className={classes.navReportTitleBox}>
                        <Typography component="span" className={classes.navReportTitle}>
                            {reportChart?.subNavLabel}
                        </Typography>
                    </Grid>
                    <Grid container item className={classnames(classes.navControls, classes.navBtnRight)}>
                        <IconButton onClick={() => goToReport("next")} className={classes.navBtnRight} size="medium">
                            <CustomIcon className={classes.navControlIcon} icon={icons.circledChevronRightThin} />
                        </IconButton>
                    </Grid>
                </Grid>
                {(reportChart?.id === "LD12" || reportChart?.id === "LD8") && (
                    <Grid item className={classes.paceDefinitionWrapper}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={shouldShowPaceDefinitions}
                                    disabled={Boolean(
                                        reportChart?.id === "LD12" &&
                                            !allChartData?.graphs.LD12?.converted?.series_labels.length
                                    )}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        setShouldShowPaceDefinitions(event.target.checked)
                                    }
                                />
                            }
                            label={intl.formatMessage({ id: "projection.pace.definitions.label" })}
                        />
                        <Box className={classes.secondaryPanel}>
                            <Button
                                className={classes.btnToInsights}
                                onClick={handleOnClickInsightsLink}
                                startIcon={<CustomIcon icon={icons.circledLightBulb} />}
                            >
                                {intl.formatMessage({
                                    id: "projection.insights.insights",
                                })}
                            </Button>
                        </Box>
                    </Grid>
                )}
            </Grid>

            <Grid container className={classes.projectionBodyWrapper}>
                {!isFetchingCharts && (
                    <Grid item ref={chartWrapper} className={classes.projectionBodyLeftSide}>
                        {!canSeeChart && (
                            <Box className={classes.proGraphMsgWrapper} data-id={reportChart?.id}>
                                <MessageUpgrade
                                    variant="type2"
                                    icon={icons.proStar}
                                    title={"pro.message.heading"}
                                    subtitle={"pro.upgrade"}
                                    descriptionTitle={"pro.message.details.heading"}
                                >
                                    {Array.isArray(chartExplainer) ? (
                                        chartExplainer.map((paragraph, index) => (
                                            <Box component="p" key={index}>
                                                {paragraph}
                                            </Box>
                                        ))
                                    ) : chartExplainer ? (
                                        <Box component="p">{chartExplainer}</Box>
                                    ) : null}
                                </MessageUpgrade>
                            </Box>
                        )}

                        {canSeeChart &&
                            reportChart?.graphComponent(width, height, {
                                shouldShowPaceDefinitions,
                                setShouldShowPaceDefinitions,
                            })}
                    </Grid>
                )}

                <Grid item ref={insightsSectionRef} className={classes.projectionBodyRightSide}>
                    <VcpImportance />
                    {reportChart?.graphInsights}
                </Grid>
            </Grid>
        </>
    );
};

export default Charts;
